import { faEdit, faEye, faSave, faTimes, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { articleActions, changelogArticleActions, chiefArticleActions } from '../../Hooks/DatabaseActions';
import { toasts } from '../../shared';
import { IconButton } from '../Buttons/Buttons';
import { useArticle, useCategory } from '../ContextProviders/AppContext';
import { useAuth } from '../ContextProviders/Auth';
import { useFirestore } from '../ContextProviders/Firebase';
import { useModalSpawn } from '../ContextProviders/ModalContext';
import { useConcreteProject } from '../ContextProviders/ProjectContext';
import { ArticleEditor } from '../Editor/ArticleEditor';
import { PlatformContentFrame } from '../PlatformContentFrame/PlatformContentFrame';
import './ArticleBodyEdit.scss';
import { ArticleBreadcrumbs } from './ArticleBreadcrumbs';
import { NotSavedArticleModal } from './NotSavedModal';
import { useLocalization } from '../ContextProviders/LocalizationContext';
import ArticleInfo from '../ArticleInfo/ArticleInfo';

interface ArticleBodyProps {
  id: string;
}
export const ArticleBodyEdit = ({ id }: ArticleBodyProps): ReactElement => {
  const localization = useLocalization();
  const article = useArticle(id);
  const category = useCategory(article?.category);
  const firestore = useFirestore();
  const project = useConcreteProject();
  const auth = useAuth();

  if (article === undefined || category === undefined) return <></>;

  const save = (content: string, articleId: string) => {
    if (article.isChief) {
      return chiefArticleActions(firestore, project.id).update(articleId, {
        content: content,
        name: article.name,
        orderIndex: article.orderIndex,
        section: article.section,
        category: article.category,
      });
    } else if (article.isChangelog) {
      return changelogArticleActions(firestore, project.id).update(articleId, {
        content: content,
        name: article.name,
        orderIndex: article.orderIndex,
        section: article.section,
        category: article.category,
      });
    }
    return articleActions(firestore, project.id, auth.displayName).update(articleId, { content: content });
  };

  return (
    <article>
      <div className="article-body editable-content">
        <EditableContent
          fId={article.fId}
          content={article.content}
          save={(content) => save(content, id)}
          key={id}
          headerInject={<ArticleBreadcrumbs id={id} />}
        />
      </div>
      {article.lastUpdated && article.lastUpdatedBy ? (
        <div className="last-edited">
          {`${localization.strings.publish.titles.lastUpdatedBy}: ${article.lastUpdatedBy}, ${article.lastUpdated
            .toDate()
            .toLocaleDateString('sv-SE')}`}
        </div>
      ) : article.lastUpdated ? (
        <div className="last-edited">
          {`${localization.strings.publish.titles.lastUpdated}: ${article.lastUpdated
            .toDate()
            .toLocaleDateString('sv-SE')}`}
        </div>
      ) : (
        ''
      )}
    </article>
  );
};

interface EditableContetProps {
  fId?: string;
  content: string;
  save: (content: string) => Promise<unknown>;
  headerInject?: ReactElement;
}
type History = {
  editableContent: {
    fId?: string;
    content: string;
    editing: boolean;
    isDirty: boolean;
  };
};

export const EditableContent = ({
  fId: _fId,
  content: _content,
  save,
  headerInject,
}: EditableContetProps): ReactElement => {
  const localization = useLocalization();
  const history = useHistory<History>();
  const spawn = useModalSpawn();
  const config = useConcreteProject();

  const [content, setContent] = useState(history.location.state?.editableContent?.content || _content);
  const [editing, setEditing] = useState(history.location.state?.editableContent?.editing || false);
  const [isDirty, setDirty] = useState<boolean>(history.location.state?.editableContent?.isDirty || false);
  const [showArticleInfo, setShowArticleInfo] = useState(false);
  const isDirtyNow = useRef(isDirty);
  isDirtyNow.current = isDirty;
  const contentNow = useRef(content);
  contentNow.current = content;

  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = useState<number>(0);

  useEffect(() => {
    //Setting this to prevent overflow from the editor
    if (editorContainerRef.current) setEditorHeight(editorContainerRef.current.clientHeight);
  }, []);

  useEffect(() => {
    history.replace(`${history.location.pathname}${history.location.hash}`, {
      ...history.location.state,
      editableContent: JSON.parse(JSON.stringify({ editing, isDirty, content })),
    });
    // eslint-disable-next-line
  }, [editing, isDirty, content]);

  const saveRef = useRef(() => save(contentNow.current));

  useEffect(() => {
    return () => {
      if (isDirtyNow.current && contentNow.current) {
        spawn(
          'articleUnsavedModal',
          <NotSavedArticleModal
            onDiscardChanges={() => {
              spawn('articleUnsavedModal', <></>);
            }}
            onSave={() => {
              saveRef
                // eslint-disable-next-line
                .current()
                .then(() => {
                  toasts.success(localization.strings.article.updateOk);
                })
                .catch(() => {
                  toasts.error(localization.strings.article.updateFailed);
                });
              spawn('articleUnsavedModal', <></>);
            }}
            onCancel={() => {
              history.goBack();
              spawn('articleUnsavedModal', <></>);
            }}
            isOpen={true}
          />,
        );
      }
    };
    // eslint-disable-next-line
  }, []);

  const reset = (rcontent: string) => {
    setContent(rcontent);
    setEditing(false);
    setDirty(false);
  };

  const saveContent = (content: string) => {
    save(content)
      .then(() => {
        toasts.success(localization.strings.article.updateOk);
        setEditing(false);
        setDirty(false);
      })
      .catch((e) => {
        toasts.error(localization.strings.article.updateFailed);
      });
  };

  return (
    <>
      <div className="editable-content-head">
        {headerInject && headerInject}
        <div className="edit-view-cancel-buttons">
          <EditViewCancel
            isDirty={isDirty}
            editing={editing}
            onEditToggle={() => setEditing((ed) => !ed)}
            onSave={() => saveContent(content)}
            onCancel={() => reset(_content)}
          />
          {config.enableTagSearch && (
            <IconButton
              onClick={() => setShowArticleInfo(!showArticleInfo)} // Toggle visibility
              theme="dark"
              icon={showArticleInfo ? faEyeSlash : faEye}
              text={
                showArticleInfo
                  ? localization.strings.article.articleInfo.hideTagEditor
                  : localization.strings.article.articleInfo.showTagEditor
              }
            />
          )}
        </div>
        {config.enableTagSearch && showArticleInfo && <ArticleInfo id={_fId} editing={editing} />}
      </div>
      <div className="editable-content-body" ref={editorContainerRef}>
        {!editing ? (
          <PlatformContentFrame body={content} bodyClass="edit-mode" articleId={_fId} editTags={true} />
        ) : (
          <ArticleEditor
            editStatus={true}
            onChange={(c) => {
              setContent(c);
              setDirty(true);
            }}
            content={content}
            maxHeight={editorHeight}
          />
        )}
      </div>
    </>
  );
};

interface EditViewCancelProps {
  isDirty: boolean;
  editing: boolean;
  onEditToggle: () => void;
  onSave: () => void;
  onCancel: () => void;
}
const EditViewCancel = ({ isDirty, editing, onEditToggle, onSave, onCancel }: EditViewCancelProps) => {
  const localization = useLocalization();

  return (
    <>
      <IconButton
        onClick={() => {
          onEditToggle();
        }}
        theme="dark"
        icon={editing ? faEye : faEdit}
        text={editing ? localization.strings.global.preview : localization.strings.global.edit}
        centerWithFlex={true}
      />

      {isDirty && (
        <>
          <IconButton
            onClick={() => {
              onSave();
            }}
            theme="dark"
            icon={faSave}
            text={localization.strings.global.save}
          />
          <IconButton
            onClick={() => {
              onCancel();
            }}
            theme="dark"
            icon={faTimes}
            text={localization.strings.global.cancel}
          />
        </>
      )}
    </>
  );
};
